import { useEffect, useRef, useState, type ChangeEvent } from "react";
import type { MunicipioCandidato } from "@/api/contrato";
import { Campo } from "@/componentes/Campo";
import { CampoDeDocumento } from "@/componentes/CampoDeDocumento";
import { Picote } from "@/componentes/Picote";
import { Selecao } from "@/componentes/Selecao";
import {
  TAMANHO_MAXIMO,
  UFS,
  recusaDoCedente,
  type CampoDoCedente,
  type ErrosDoCedente,
  type ResultadoDaValidacao,
  type ValoresDoCedente,
} from "./cedente-formulario";

const OPCOES_DE_UF: readonly (readonly [string, string])[] = [
  ["", "—"],
  ...UFS.map((uf) => [uf, uf] as const),
];

/**
 * O estado do formulário, igual na criação e na edição.
 *
 * Fechar o modal desmonta quem usa este hook, e é isso que limpa campos e recusa: não
 * há estado para zerar, e nada da tentativa anterior sobrevive à reabertura.
 */
export function useFormularioDeCedente(inicial: ValoresDoCedente) {
  const [valores, setValores] = useState(inicial);
  const [erros, setErros] = useState<ErrosDoCedente>({});
  const [erroGeral, setErroGeral] = useState<string | undefined>(undefined);
  const [candidatos, setCandidatos] = useState<readonly MunicipioCandidato[]>([]);
  const [tentativa, setTentativa] = useState(0);

  function mudar(campo: CampoDoCedente, valor: string) {
    /*
     * Cidade ou UF novas invalidam o código escolhido e a lista de candidatos: reenviar
     * o código velho seria recusado como `codigo_ibge_diverge_da_cidade`.
     */
    const mudouMunicipio = campo === "endereco.cidade" || campo === "endereco.uf";

    setValores((atuais) => ({
      ...atuais,
      [campo]: valor,
      ...(mudouMunicipio && { "endereco.codigo_ibge": "" }),
    }));
    // O erro some quando o operador mexe no campo: vermelho depois da correção diria
    // que a correção não serviu.
    setErros((atuais) => ({
      ...atuais,
      [campo]: undefined,
      ...(mudouMunicipio && { "endereco.codigo_ibge": undefined }),
    }));
    if (mudouMunicipio) setCandidatos([]);
  }

  /** O candidato vira a cidade da tela, e a seleção fica à vista com a escolha. */
  function escolherMunicipio(codigo: string) {
    const candidato = candidatos.find((municipio) => municipio.codigo_ibge === codigo);

    setValores((atuais) => ({
      ...atuais,
      "endereco.codigo_ibge": codigo,
      ...(candidato !== undefined && { "endereco.cidade": candidato.nome }),
    }));
    setErros((atuais) => ({ ...atuais, "endereco.codigo_ibge": undefined }));
  }

  /** Devolve o corpo pronto para enviar, ou marca os campos e devolve `null`. */
  function conferir<C>(resultado: ResultadoDaValidacao<C>): C | null {
    setErroGeral(undefined);
    if (resultado.ok) {
      // A recusa anterior não vale mais: a próxima resposta é sobre o que vai agora.
      setErros({});
      return resultado.corpo;
    }

    setErros(resultado.erros);
    setTentativa((atual) => atual + 1);
    return null;
  }

  function recusar(falha: unknown) {
    const recusa = recusaDoCedente(falha, candidatos);
    setErros(recusa.erros);
    setErroGeral(recusa.geral);
    setCandidatos(recusa.candidatos);
    setTentativa((atual) => atual + 1);
  }

  return {
    valores,
    erros,
    erroGeral,
    candidatos,
    tentativa,
    mudar,
    escolherMunicipio,
    conferir,
    recusar,
  };
}

export type FormularioDoCedente = ReturnType<typeof useFormularioDeCedente>;

/*
 * Os campos do cedente, compartilhados por criação e edição.
 *
 * O documento leva máscara e não confere dígito (`CampoDeDocumento`). `required` nativo
 * fica de fora: a bolha do navegador acusa um campo por vez e sai do padrão de ícone
 * mais texto. Os obrigatórios são `aria-required`, e quem acusa é a validação.
 *
 * O CEP é só um campo: o painel não consulta serviço externo, e quem resolve o município
 * é a API, por cidade e UF.
 */
export function FormularioDeCedente({
  formulario,
  documentoFixo = false,
}: {
  formulario: FormularioDoCedente;
  /** Na edição: o documento não muda, e a API o recusa no `PATCH` (doc 16 §15.1). */
  documentoFixo?: boolean;
}) {
  const { valores, erros, candidatos, tentativa, mudar, escolherMunicipio } = formulario;
  const raiz = useRef<HTMLDivElement>(null);

  // O modal rola: sem isto, a recusa de um campo lá em cima não aparece para quem
  // apertou o botão lá embaixo. Só depois de enviar, e nunca enquanto digita.
  useEffect(() => {
    if (tentativa === 0) return;
    raiz.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [tentativa]);

  function ligar(campo: CampoDoCedente) {
    return {
      value: valores[campo],
      onChange: (evento: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        mudar(campo, evento.target.value),
      erro: erros[campo],
    };
  }

  return (
    <div ref={raiz} className="contents">
      {documentoFixo ? (
        <CampoDeDocumento
          valor={valores.documento}
          aoMudar={() => undefined}
          readOnly
          dica="A TecnoSpeed não altera CPF nem CNPJ de cedente."
        />
      ) : (
        <CampoDeDocumento
          valor={valores.documento}
          aoMudar={(valor) => mudar("documento", valor)}
          erro={erros.documento}
          aria-required
          autoFocus
        />
      )}

      <Campo
        rotulo="Razão social"
        {...ligar("razao_social")}
        maxLength={TAMANHO_MAXIMO.razao_social}
        autoFocus={documentoFixo}
        aria-required
      />

      <Campo
        rotulo="Nome fantasia"
        {...ligar("nome_fantasia")}
        maxLength={TAMANHO_MAXIMO.nome_fantasia}
      />

      <div className="grid grid-cols-[3fr_2fr] gap-3">
        <Campo
          rotulo="E-mail"
          inputMode="email"
          autoComplete="off"
          {...ligar("email")}
          maxLength={TAMANHO_MAXIMO.email}
        />

        <Campo
          rotulo="Telefone"
          inputMode="tel"
          autoComplete="off"
          {...ligar("telefone")}
          maxLength={TAMANHO_MAXIMO.telefone}
        />
      </div>

      <Picote />

      <div role="group" aria-label="Endereço" className="contents">
        <div className="grid grid-cols-[2fr_5fr] gap-3">
          <Campo
            rotulo="CEP"
            inputMode="numeric"
            autoComplete="off"
            {...ligar("endereco.cep")}
            maxLength={TAMANHO_MAXIMO["endereco.cep"]}
            aria-required
          />

          <Campo
            rotulo="Logradouro"
            autoComplete="off"
            {...ligar("endereco.logradouro")}
            maxLength={TAMANHO_MAXIMO["endereco.logradouro"]}
            aria-required
          />
        </div>

        <div className="grid grid-cols-[2fr_5fr] gap-3">
          <Campo
            rotulo="Número"
            autoComplete="off"
            {...ligar("endereco.numero")}
            maxLength={TAMANHO_MAXIMO["endereco.numero"]}
            aria-required
          />

          <Campo
            rotulo="Complemento"
            autoComplete="off"
            {...ligar("endereco.complemento")}
            maxLength={TAMANHO_MAXIMO["endereco.complemento"]}
          />
        </div>

        <Campo
          rotulo="Bairro"
          autoComplete="off"
          {...ligar("endereco.bairro")}
          maxLength={TAMANHO_MAXIMO["endereco.bairro"]}
          aria-required
        />

        <div className="grid grid-cols-[2fr_5fr] gap-3">
          <Selecao rotulo="UF" opcoes={OPCOES_DE_UF} {...ligar("endereco.uf")} aria-required />
          <Campo rotulo="Cidade" autoComplete="off" {...ligar("endereco.cidade")} aria-required />
        </div>

        {candidatos.length > 0 && (
          <Selecao
            rotulo="Município"
            opcoes={[
              ["", "Escolha"],
              ...candidatos.map(({ codigo_ibge, nome }) => [codigo_ibge, nome] as const),
            ]}
            value={valores["endereco.codigo_ibge"]}
            onChange={(evento) => escolherMunicipio(evento.target.value)}
            erro={erros["endereco.codigo_ibge"]}
          />
        )}
      </div>
    </div>
  );
}
